import { DisplaySubject } from "pages/SubjectManagement/hook";
import { useConsoleState } from "providers/ConsoleStateProvider";
import React from "react";
import { useHistory } from "react-router";

import {
  StyledMemoWrapper,
  StyledNoDataTypography,
  StyledStar,
  StyledStarFill,
  StyledMarkedTbodyTd,
} from "./styled";
import { Typography } from "components/parts/Typography/Typography";
import { StatusLabel } from "components/parts/Label/StatusLabel";
import {
  StyledRightestTheadTh,
  StyledTable,
  StyledTbody,
  StyledTbodyTd,
  StyledTbodyTr,
  StyledThead,
  StyledTheadTh,
  StyledTheadTr,
  TableColumnProps,
} from "components/templates/Table/styled";
import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { CreateMarkedSubjectInput, InterviewType } from "types/graphqlapi";
import { InterviewTypeIcon } from "components/parts/InterviewTypeIcon";

type SubjectListProps = {
  subjectList: DisplaySubject[];
  markedSubjectList: CreateMarkedSubjectInput[];
  selectSubject: (subject: DisplaySubject) => void;
  onChangeSubjectMark: (subject: DisplaySubject) => Promise<void>;
};

export const SubjectList: React.FC<SubjectListProps> = ({
  subjectList,
  markedSubjectList,
  selectSubject,
  onChangeSubjectMark,
}) => {
  const history = useHistory();
  const { setSubject, setGroup, isOneGroupUser, subjectFilter } =
    useConsoleState();
  const moveInterviewManage = (subject: DisplaySubject) => {
    setGroup(subject.group_id, subject.group_name);
    setSubject({
      id: subject.id,
      status: subject.status,
      subjectType: subject.interview_type || InterviewType.ONE_TIME,
    });
    history.replace(`/interview`);
  };

  const isMarked = (subject: DisplaySubject) =>
    markedSubjectList
      .map((markedSubject) => markedSubject.subject_id)
      .includes(subject.id);

  const currentSubjectList = React.useMemo(
    () =>
      [...subjectList]
        // ステータスフィルター
        .filter((subject) => {
          if (subjectFilter.status === "ALL") {
            return subject;
          } else {
            return subject.status === subjectFilter.status;
          }
        })
        // チェックされているかのフィルター
        .filter((subject) => {
          if (subjectFilter.marked === "ALL") {
            return subject;
          } else if (subjectFilter.marked === "MARKED") {
            return markedSubjectList
              .map((markedSubject) => markedSubject.subject_id)
              .includes(subject.id);
          }
        })
        // グループフィルター
        .filter((subject) => {
          if (subjectFilter.group === "ALL") {
            return subject;
          } else if (subjectFilter.group && subjectFilter.isSelectGroup) {
            return subjectFilter.group === subject.group_id;
          } else {
            return subject;
          }
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjectList, subjectFilter]
  );

  const subjectTableColumn = [
    {
      columnId: "status",
      columnType: "statusLabel",
      textAlign: "center",
      minWidth: "7em",
      text: "ステータス",
      width: "300px",
    },
    !subjectFilter.isSelectGroup &&
      !isOneGroupUser && {
        columnId: "group_name",
        columnType: "typography",
        textAlign: "left",
        minWidth: "8em",
        text: "グループ名",
        width: "300px",
      },
    {
      columnId: "subject_number",
      columnType: "typography",
      textAlign: "center",
      minWidth: "5em",
      text: "案件ID",
      width: "300px",
    },
    {
      columnId: "subject_name",
      columnType: "typography",
      textAlign: "left",
      minWidth: "14em",
      text: "案件名",
      width: "1000px",
    },
    {
      columnId: "interview_type",
      columnType: "typeLabel",
      textAlign: "center",
      minWidth: "5em",
      text: "タイプ",
      width: "1000px",
    },
    {
      columnId: "first_interview_date",
      columnType: "typography",
      textAlign: "left",
      minWidth: "6em",
      text: "実査開始日",
      width: "300px",
    },
    {
      columnId: "last_interview_date",
      columnType: "typography",
      textAlign: "left",
      minWidth: "6em",
      text: "実査終了日",
      width: "300px",
    },
    {
      columnId: "display_expiration_date",
      columnType: "typography",
      textAlign: "left",
      minWidth: "6em",
      text: "データ削除日",
      width: "300px",
    },
    {
      columnId: "main_user_name_group",
      columnType: "typography",
      textAlign: "left",
      minWidth: "8em",
      text: "担当者1",
      width: "300px",
    },
    {
      columnId: "main_user_name_asmarq",
      columnType: "typography",
      textAlign: "left",
      minWidth: "8em",
      text: "担当者2",
      width: "300px",
    },
    {
      columnId: "memo",
      columnType: "typography",
      textAlign: "left",
      minWidth: "14em",
      text: "メモ",
      width: "300px",
    },
    {
      columnId: "editButton",
      columnType: "button",
      textAlign: "center",
      minWidth: "8em",
      text: "編集",
      width: "300px",
    },
  ] as TableColumnProps[];

  return (
    <>
      <StyledTable>
        <StyledThead>
          <StyledTheadTr>
            <StyledTheadTh textAlign={"center"} minWidth={"2em"} width={"40px"}>
              <Typography text={""} />
            </StyledTheadTh>
            {subjectTableColumn.map((column, index) =>
              subjectTableColumn.length === index + 1 ? (
                <StyledRightestTheadTh
                  key={`tableHeaderColumn${column.columnId}`}
                  textAlign={column.textAlign}
                  width={column.width}
                  minWidth={column.minWidth}
                >
                  <Typography text={column.text} size="small" />
                </StyledRightestTheadTh>
              ) : (
                <StyledTheadTh
                  key={`tableHeaderColumn${column.columnId}`}
                  textAlign={column.textAlign}
                  width={column.width}
                  minWidth={column.minWidth}
                >
                  <Typography text={column.text} size="small" />
                </StyledTheadTh>
              )
            )}
          </StyledTheadTr>
        </StyledThead>
        <StyledTbody>
          {currentSubjectList.length === 0 ? (
            <tr>
              <td>
                <StyledNoDataTypography text="案件がありません" />
              </td>
            </tr>
          ) : (
            currentSubjectList.map((subject) => (
              <StyledTbodyTr
                key={subject.id}
                onClick={() => {
                  moveInterviewManage(subject);
                }}
              >
                <StyledMarkedTbodyTd
                  textAlign={"center"}
                  minWidth={"2em"}
                  width={"40px"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangeSubjectMark(subject);
                  }}
                >
                  {isMarked(subject) ? <StyledStarFill /> : <StyledStar />}
                </StyledMarkedTbodyTd>
                {subjectTableColumn.map((column) => (
                  <StyledTbodyTd
                    key={`${column.columnId}Of${subject.id}`}
                    textAlign={column.textAlign}
                    minWidth={column.minWidth}
                    width={column.width}
                  >
                    {column.columnType === "statusLabel" ? (
                      <StatusLabel status={subject.status} />
                    ) : column.columnType === "typeLabel" ? (
                      <InterviewTypeIcon
                        interviewType={subject.interview_type}
                        size={24}
                      />
                    ) : column.columnType === "typography" ? (
                      column.columnId === "memo" ? (
                        <StyledMemoWrapper>
                          <Typography
                            text={subject.memo ? subject.memo : ""}
                            size="small"
                          />
                        </StyledMemoWrapper>
                      ) : (
                        (() => {
                          const key = column.columnId as keyof typeof subject;
                          const subjectContent = subject[key]?.toString();
                          if (typeof subjectContent === "string")
                            return (
                              <Typography text={subjectContent} size="small" />
                            );
                        })()
                      )
                    ) : column.columnType === "button" ? (
                      <TextWithIconButton
                        text={"編集"}
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectSubject(subject);
                        }}
                        iconName={TextWithIconTypes.Edit}
                      />
                    ) : (
                      ""
                    )}
                  </StyledTbodyTd>
                ))}
              </StyledTbodyTr>
            ))
          )}
        </StyledTbody>
      </StyledTable>
    </>
  );
};
